import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import {
  AdminPanelSettings,
  AutoAwesome,
  Business,
  Download,
  EmojiEvents,
  Flag,
  Groups,
  Key,
  Menu as MenuIcon,
  People,
  TableRows,
  Task,
  UploadFile,
} from "@mui/icons-material";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAccessControl } from "../context/AccessControlContext";

const NAV_ITEMS = [
  { to: "/admin/ai", label: "AI Assistant", icon: <AutoAwesome /> },
  { to: "/admin/entries", label: "Entries", icon: <TableRows /> },
  { to: "/admin/flags", label: "Flags", icon: <Flag /> },
  { to: "/admin/players", label: "Players", icon: <People /> },
  { to: "/admin/actions", label: "Actions", icon: <AdminPanelSettings /> },
  { to: "/admin/tasks", label: "Task List", icon: <Task /> },
  { to: "/admin/organisations", label: "Organisations", icon: <Business /> },
  { to: "/admin/clubs", label: "Clubs", icon: <Groups /> },
  { to: "/admin/ranking-setup", label: "Ranking Setup", icon: <EmojiEvents /> },
  { to: "/admin/member-import", label: "Member Import", icon: <UploadFile /> },
  { to: "/admin/api-keys", label: "API Keys", icon: <Key /> },
  { to: "/admin/exports", label: "Exports", icon: <Download /> },
];

// One list, shared by the desktop sidebar and the mobile drawer.
function AdminNavList() {
  return (
    <List sx={{ p: 0 }}>
      {NAV_ITEMS.map((item) => {
        const isAi = item.to === "/admin/ai";

        return (
          <NavLink
            key={item.to}
            to={item.to}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {({ isActive }) => (
              <ListItemButton
                selected={isActive}
                sx={{
                  borderRadius: "0.375rem",
                  mb: 0.5,
                  ...(isAi && {
                    border: "1.5px solid #a855f7",
                    color: "#a855f7",
                    "&:hover": { borderColor: "#a855f7" },
                    "& .MuiListItemIcon-root": { color: "#a855f7" },
                    "& .MuiListItemText-primary": { fontWeight: 700 },
                  }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            )}
          </NavLink>
        );
      })}
    </List>
  );
}

function AdminLayout() {
  const { isAdmin, isLoading } = useAccessControl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Nothing else in the app resets scroll on navigation, so switching
  // between admin pages would otherwise keep the previous scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <div>Loading...</div>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Access Denied: Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        py: 4,
        // Full-width section: the admin pages carry no horizontal gutter of
        // their own, so this layout provides one.
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Mobile menu trigger */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <IconButton
          aria-label="Open admin menu"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Desktop sidebar */}
      <Paper
        sx={{
          width: 240,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: { md: "sticky" },
          top: { md: 16 },
          display: { xs: "none", md: "block" },
          p: 1,
        }}
      >
        <AdminNavList />
      </Paper>

      {/* Mobile drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box
          sx={{ width: 240, p: 1 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        >
          <AdminNavList />
        </Box>
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminLayout;
